import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Container,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import DescriptionIcon from "@mui/icons-material/Description";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import AirlineSeatReclineExtraIcon from "@mui/icons-material/AirlineSeatReclineExtra";
import { QRCodeCanvas } from "qrcode.react";
import API_BASE_URL from "../config";

const BookTickets = () => {
  const { state } = useLocation();
  const event = state?.event;
  const { id } = useParams(); // Fetch event_id from the URL

  const [submitted, setSubmitted] = useState(false);
  const [qrData, setQrData] = useState("");
  const [selectedSeats, setSelectedSeats] = useState(1);

  const [user, setUser] = useState({ id: 1 }); // Replace with actual logged-in user if needed
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    location: "",
  });

  const [eventDetails, setEventDetails] = useState(null);

  const formattedDate = eventDetails?.event_date
    ? new Date(eventDetails.event_date).toLocaleDateString()
    : "";

  // Fetch event details by ID
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/events/${id}`
        );
        setEventDetails(response.data);
      } catch (error) {
        console.error("❌ Error fetching event details:", error);
      }
    };

    if (id) fetchEvent();
  }, [id]);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/${user.id}`
        );
        setUserData({
          name: response.data.name,
          email: response.data.email,
          location: response.data.city,
        });
      } catch (error) {
        console.error("❌ Error fetching user data:", error);
      }
    };

    if (user && user.id) {
      fetchUserData();
    }
  }, [user]);

  const handlePayment = async (bookingData) => {
    const amountInPaise = bookingData.price * 100;

    const options = {
      key: eventDetails?.razorpay_key_id,
      amount: amountInPaise,
      currency: "INR",
      name: eventDetails?.name,
      description: `Event Ticket Booking - ${eventDetails?.name}`,

      handler: async function (response) {
        console.log("✅ Payment Success:", response);

        const paymentPayload = {
          user_id: bookingData.user_id,
          event_id: bookingData.event_id,
          provider_id: bookingData.provider_id,
          amount: bookingData.price,
          payment_method: "razorpay",
          transaction_id: response.razorpay_payment_id,
          status: "success",
          payment_type: "ticket",
          opportunity_type: "event",
        };

        try {
          await axios.post(
            `${API_BASE_URL}/events-payments`,
            paymentPayload
          );
          console.log("✅ Payment recorded successfully");

          const qrContent = {
            ...bookingData,
            transactionId: response.razorpay_payment_id,
            paymentStatus: "success",
          };

          setQrData(JSON.stringify(qrContent));
          setSubmitted(true);

          // ✅ Save QR code to backend
          const bookingResponse = await axios.get(
            `${API_BASE_URL}/event-bookings/latest?user_id=${bookingData.user_id}&event_id=${bookingData.provider_id}`
          );

          const bookingId = bookingResponse.data.booking_id;

          await axios.post(
            `${API_BASE_URL}/events-payments/save-qr`,
            {
              booking_id: bookingId,
              qr_type: "active",
              qr_data: JSON.stringify(qrContent),
            }
          );

          console.log("✅ QR code saved to backend");
        } catch (err) {
          console.error("❌ Payment record or QR save failed", err);
        }
      },
      prefill: {
        name: userData.name,
        email: userData.email,
        contact: "0000000000",
      },
      notes: {
        address: userData.location,
      },
      theme: {
        color: "#3399cc",
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const totalAmount = selectedSeats * eventDetails?.event_fees;

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const bookingPayload = {
      user_id: user.id,
      provider_id: eventDetails?.id,
      event_id: eventDetails?.id, // ✅ Provide event_id explicitly
      event_name: eventDetails?.name,
      organizer_name: eventDetails?.organizer,
      description: eventDetails?.description,
      event_date: eventDetails?.event_date,
      price: totalAmount,
      total_no_of_seats: eventDetails?.total_no_of_seats,
      available_seats: eventDetails?.available_tickets, // ✅ Add this
      qr_type: "active",
      location: eventDetails?.location,
      booked_tickets_myself: selectedSeats,
      booked_tickets_tillnow: eventDetails?.booked_tickets + selectedSeats, // ✅ Add this
      payment_status: "pending",
    };
  
    try {
      const response = await axios.post(
        `${API_BASE_URL}/event-bookings`,
        bookingPayload
      );
      console.log("✅ Booking created:", response.data);
  
      handlePayment(bookingPayload); // This will now run
    } catch (err) {
      console.error("❌ Booking failed", err);
    }
  };
  

  if (!eventDetails) {
    return (
      <Container maxWidth="sm" sx={{ mt: 5 }}>
        <Typography variant="h6" color="error">
          Error: No event data available.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 5, mb: 4 }}>
      <Card sx={{ p: 4, boxShadow: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h5" color="#42326e" textAlign="center" mb={3}>
            🎉 Book a Ticket for {eventDetails?.name}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Event Name"
                name="name"
                value={eventDetails?.name}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <PersonIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Organizer Name"
                name="organizer"
                value={eventDetails?.organizer}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <LocationOnIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={eventDetails?.location}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CalendarTodayIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Event Date"
                name="event_date"
                value={formattedDate}
                InputLabelProps={{ shrink: true }}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Event Fee (₹)"
                name="event_fees"
                type="number"
                value={eventDetails?.event_fees}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <DescriptionIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={eventDetails?.description}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventSeatIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Total Number of Seats"
                name="total_no_of_seats"
                type="number"
                value={eventDetails?.total_no_of_seats}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventSeatIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Number of Seats Available"
                name="no_of_seats_available"
                type="number"
                value={eventDetails?.available_tickets}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <AirlineSeatReclineExtraIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Booked Tickets"
                name="booked_seats"
                type="number"
                value={eventDetails?.booked_tickets}
                required
                disabled
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <EventSeatIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Select Number of Seats"
                type="number"
                name="selected_seats"
                value={selectedSeats}
                onChange={(e) => setSelectedSeats(parseInt(e.target.value))}
                required
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <CurrencyRupeeIcon sx={{color:"#42326e"}} />
              <TextField
                fullWidth
                label="Total Amount Payable"
                value={selectedSeats * eventDetails?.event_fees}
                disabled
              />
            </Box>

            <Button
              variant="contained"
              color="success"
              fullWidth
              type="submit"
              disabled={submitted}
            >
              {submitted ? "Processing..." : "Proceed to Payment"}
            </Button>
          </form>

          {qrData && (
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="h6" color="#42326e" mb={2}>
                Your Ticket QR Code:
              </Typography>
              <QRCodeCanvas value={qrData} size={256} />
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default BookTickets;
